import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 书法艺术 — 竖屏「水墨意境」风格
 *
 * 核心亮点：
 * - 中央 SVG 书法字笔画动画（strokeDasharray/strokeDashoffset 模拟毛笔书写）
 * - 水墨晕染背景（径向渐变模拟墨迹）
 * - 字形结构分析（九宫格 + 笔画顺序编号）
 * - 书法家信息卡片
 * - 书体对比面板
 *
 * 尺寸：1080x1920 竖屏
 */

interface StrokePrinciple {
  name: string;
  description: string;
}

interface StyleItem {
  name: string;
  description: string;
}

interface CalligraphyArtTemplateProps {
  topic: string;
  subtitle: string;
  character: string;
  calligrapher: string;
  era: string;
  style: string;
  strokes: StrokePrinciple[];
  appreciation: string;
  keyFact: string;
  styleComparison: StyleItem[];
}

const DEFAULT_PROPS: CalligraphyArtTemplateProps = {
  topic: '永字八法 — 书法入门',
  subtitle: 'The Eight Principles of Yǒng',
  character: '永',
  calligrapher: '王羲之',
  era: '东晋',
  style: '行书',
  strokes: [
    { name: '侧(点)', description: '如鸟翻然而下' },
    { name: '勒(横)', description: '如勒马之缰' },
    { name: '努(竖)', description: '如弓弩之发' },
    { name: '趯(钩)', description: '如人踢脚' },
    { name: '策(提)', description: '如策马之鞭' },
    { name: '掠(撇)', description: '如篦之掠发' },
    { name: '啄(短撇)', description: '如鸟之啄物' },
    { name: '磔(捺)', description: '如刀之割物' },
  ],
  appreciation:
    '「永」字浓缩了汉字基本笔画的精髓，八法兼备。王羲之《兰亭序》开篇即以此字，笔势流畅，气韵生动，被誉为书法入门的必修课。',
  keyFact: '永字八法相传源于智永和尚，经王羲之发扬光大，成为楷书笔法的基础纲领。',
  styleComparison: [
    { name: '楷书', description: '端庄工整，法度严谨' },
    { name: '行书', description: '流畅自然，介于楷草之间' },
    { name: '草书', description: '奔放恣意，一气呵成' },
  ],
};

/** Ink blot for background wash effect */
const InkBlot: React.FC<{
  cx: number;
  cy: number;
  delay: number;
  maxR: number;
}> = ({ cx, cy, delay, maxR }) => {
  const frame = useCurrentFrame();
  const r = interpolate(
    frame,
    [delay, delay + 80],
    [0, maxR],
    { extrapolateRight: 'clamp' }
  );
  const opacity = interpolate(
    frame,
    [delay, delay + 30, delay + 100, delay + 150],
    [0, 0.08, 0.05, 0.03],
    { extrapolateRight: 'clamp' }
  );
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill="#1a1a1a"
      opacity={opacity}
    />
  );
};

/** SVG paths for 永 character - 8 strokes (永字八法) */
const YONG_STROKE_PATHS: { d: string; length: number }[] = [
  { d: 'M 420 280 Q 440 260 460 280', length: 60 },
  { d: 'M 340 320 L 500 320', length: 160 },
  { d: 'M 420 320 L 420 680', length: 360 },
  { d: 'M 420 680 Q 440 720 480 700', length: 80 },
  { d: 'M 420 420 L 520 320', length: 140 },
  { d: 'M 340 400 Q 280 520 320 640', length: 280 },
  { d: 'M 500 400 Q 460 480 480 560', length: 180 },
  { d: 'M 420 560 Q 520 620 580 560', length: 200 },
];

export const CalligraphyArtTemplate: React.FC<
  Partial<CalligraphyArtTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgReveal = interpolate(frame, [0, 50], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleProgress = spring({
    frame: frame - 15,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const charStart = 60;
  const strokeDuration = 25;
  const strokeGap = 8;

  const gridStart = charStart + YONG_STROKE_PATHS.length * (strokeDuration + strokeGap) + 40;
  const gridProgress = spring({
    frame: frame - gridStart,
    fps,
    config: { damping: 16, stiffness: 100 },
  });

  const calligrapherStart = gridStart + 50;
  const calligrapherProgress = spring({
    frame: frame - calligrapherStart,
    fps,
    config: { damping: 14, stiffness: 120 },
  });

  const styleStart = calligrapherStart + 60;
  const styleProgress = interpolate(
    frame,
    [styleStart, styleStart + 40],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  const appreciationStart = styleStart + 80;
  const appreciationProgress = interpolate(
    frame,
    [appreciationStart, appreciationStart + 30],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  const factStart = appreciationStart + 50;
  const factProgress = interpolate(
    frame,
    [factStart, factStart + 30],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #f5f0e8 0%, #ede5d8 50%, #e8e0d0 100%)',
        fontFamily:
          '"Noto Serif SC", "STSong", "SimSun", "Source Han Serif CN", serif',
        color: '#1a1a1a',
        overflow: 'hidden',
      }}
    >
      {/* Ink wash background */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <defs>
          <radialGradient id="inkBlot1" cx="30%" cy="20%" r="50%">
            <stop offset="0%" stopColor="#1a1a1a" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#1a1a1a" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="inkBlot2" cx="70%" cy="60%" r="50%">
            <stop offset="0%" stopColor="#1a1a1a" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#1a1a1a" stopOpacity="0" />
          </radialGradient>
        </defs>
        <InkBlot cx={200} cy={400} delay={0} maxR={400} />
        <InkBlot cx={800} cy={350} delay={15} maxR={350} />
        <InkBlot cx={500} cy={1200} delay={25} maxR={380} />
        <InkBlot cx={150} cy={1500} delay={10} maxR={300} />
        <InkBlot cx={900} cy={1600} delay={20} maxR={320} />
      </svg>

      {/* Rice paper texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(circle at 2px 2px, rgba(139,119,80,0.04) 1px, transparent 0)',
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }}
      />

      {/* ========== HEADER ========== */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -12}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 70,
            fontWeight: 800,
            margin: 0,
            letterSpacing: '0.12em',
            color: '#1a1a1a',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 40,
            color: '#5a4a3a',
            marginTop: 22,
            letterSpacing: '0.05em',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ========== CENTRAL CHARACTER + SVG STROKE ANIMATION ========== */}
      <div
        style={{
          position: 'absolute',
          top: 200,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 600,
          height: 500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {/* SVG stroke animation disabled
        <svg
          viewBox="300 200 400 550"
          width={400}
          height={550}
          style={{
            position: 'relative',
            overflow: 'visible',
          }}
        >
          {YONG_STROKE_PATHS.map((stroke, i) => {
            const strokeStart = charStart + i * (strokeDuration + strokeGap);
            const drawProgress = interpolate(
              frame,
              [strokeStart, strokeStart + strokeDuration],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );
            const strokeDashoffset = stroke.length * (1 - drawProgress);

            return (
              <path
                key={i}
                d={stroke.d}
                fill="none"
                stroke="#1a1a1a"
                strokeWidth={12}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={stroke.length}
                strokeDashoffset={strokeDashoffset}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                }}
              />
            );
          })}
        </svg>
        */}

        {/* Large character display (fallback/overlay for emphasis) */}
        <div
          style={{
            position: 'absolute',
            fontSize: 320,
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(26,26,26,0.15)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {p.character}
        </div>
      </div>

      {/* ========== CHARACTER STRUCTURE GRID (九宫格 + 笔画顺序) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 720,
          left: 80,
          right: 80,
          opacity: gridProgress,
          transform: `translateY(${(1 - gridProgress) * 20}px)`,
        }}
      >
        <div
          style={{
            fontSize: 50,
            fontWeight: 700,
            color: '#5a4a3a',
            marginBottom: 30,
            letterSpacing: '0.15em',
          }}
        >
          ◆ 永字八法 · 笔画结构
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
          }}
        >
          {p.strokes.map((s, i) => {
            const itemStart = gridStart + 30 + i * 15;
            const itemOpacity = interpolate(
              frame,
              [itemStart, itemStart + 20],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );
            return (
              <div
                key={i}
                style={{
                  padding: '14px 16px',
                  background: 'rgba(26,26,26,0.04)',
                  border: '1px solid rgba(26,26,26,0.1)',
                  borderRadius: 10,
                  opacity: itemOpacity,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: '#c0392b',
                      color: '#fff',
                      fontSize: 30,
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {i + 1}
                  </span>
                  <span
                    style={{
                      fontSize: 30,
                      fontWeight: 700,
                      color: '#1a1a1a',
                    }}
                  >
                    {s.name}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 26,
                    color: '#5a4a3a',
                    lineHeight: 1.4,
                    paddingLeft: 38,
                  }}
                >
                  {s.description}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== CALLIGRAPHER INFO CARD ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1120,
          left: 80,
          right: 80,
          opacity: calligrapherProgress,
          transform: `translateX(${(1 - calligrapherProgress) * -30}px)`,
        }}
      >
        <div
          style={{
            padding: '20px 28px',
            background: 'rgba(26,26,26,0.05)',
            border: '2px solid rgba(26,26,26,0.12)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 24,
          }}
        >
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #c0392b 0%, #a93226 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
              fontWeight: 900,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            {p.calligrapher.substring(0, 1)}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: '#1a1a1a',
                marginBottom: 4,
                marginLeft: 18
              }}
            >
              {p.calligrapher}
            </div>
            <div
              style={{
                fontSize: 30,
                color: '#5a4a3a',
                display: 'flex',
                gap: 16,
              }}
            >
              <span>〔{p.era}〕</span>
              <span style={{ color: '#c0392b', fontWeight: 600 }}>{p.style}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ========== STYLE COMPARISON PANEL ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1280,
          left: 80,
          right: 80,
          opacity: styleProgress,
        }}
      >
        <div
          style={{
            fontSize: 40,
            fontWeight: 700,
            color: '#5a4a3a',
            marginBottom: 20,
            letterSpacing: '0.1em',
          }}
        >
          ◆ 书体对比
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {p.styleComparison.map((item, i) => {
            const delay = styleStart + 20 + i * 15;
            const itemOpacity = interpolate(
              frame,
              [delay, delay + 25],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );
            const isCurrent = item.name === p.style;
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: '16px 20px',
                  background: isCurrent
                    ? 'rgba(192,57,43,0.08)'
                    : 'rgba(26,26,26,0.04)',
                  border: isCurrent
                    ? '2px solid rgba(192,57,43,0.4)'
                    : '1px solid rgba(26,26,26,0.1)',
                  borderRadius: 12,
                  opacity: itemOpacity,
                }}
              >
                <div
                  style={{
                    fontSize: 36,
                    fontWeight: 700,
                    color: isCurrent ? '#c0392b' : '#1a1a1a',
                    marginBottom: 6,
                  }}
                >
                  {item.name}
                </div>
                <div
                  style={{
                    fontSize: 30,
                    color: '#5a4a3a',
                    lineHeight: 1.4,
                  }}
                >
                  {item.description}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== APPRECIATION ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1550,
          left: 80,
          right: 80,
          opacity: appreciationProgress,
        }}
      >
        <div
          style={{
            fontSize: 40,
            fontWeight: 700,
            color: '#5a4a3a',
            marginBottom: 12,
            letterSpacing: '0.1em',
          }}
        >
          ◆ 赏析
        </div>
        <div
          style={{
            fontSize: 30,
            lineHeight: 1.7,
            color: '#3d3328',
          }}
        >
          {p.appreciation}
        </div>
      </div>

      {/* ========== KEY FACT ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1580,
          left: 80,
          right: 80,
          opacity: factProgress,
        }}
      >
        <div
          style={{
            padding: '16px 24px',
            background: 'rgba(192,57,43,0.06)',
            border: '1px solid rgba(192,57,43,0.2)',
            borderRadius: 12,
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: '#c0392b',
              marginBottom: 8,
            }}
          >
            💡 书法小知识
          </div>
          <div
            style={{
              fontSize: 20,
              lineHeight: 1.6,
              color: '#3d3328',
            }}
          >
            {p.keyFact}
          </div>
        </div>
      </div>

      {/* Seal stamp (印章) */}
      <div
        style={{
          position: 'absolute',
          bottom: 100,
          right: 80,
          opacity: interpolate(
            frame,
            [factStart + 30, factStart + 50],
            [0, 0.85],
            { extrapolateRight: 'clamp' }
          ),
          transform: 'rotate(-6deg)',
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            border: '3px solid #c0392b',
            borderRadius: 6,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 24,
            fontWeight: 900,
            color: '#c0392b',
            letterSpacing: '0.08em',
          }}
        >
          {p.calligrapher.substring(0, 2)}
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 600], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #5a4a3a, #c0392b)',
        }}
      />
    </AbsoluteFill>
  );
};
